import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { api, formatApiErrorDetail } from '../utils/api';
import {
  Save, Plus, Trash2, Copy, AlertTriangle, FileText, ArrowLeft, Loader2,
  ImageIcon, X,
} from 'lucide-react';
import { toast } from 'sonner';
import BACKEND_BASE from '../utils/backend';

const EMPTY_LINE = {
  id: null,
  plan_id: '',
  zone_id: '',
  surface_id: null,
  surface_item: '',
  product_id: null,
  dosage: '',
  temperature_instruction: '',
  method_id: null,
  frequency_id: null,
  control_id: null,
  control: '',
  responsible: 'Equipe',
  sort_order: 0,
  icon_id: null,
  is_zone_title: false,
  zone_title_text: '',
};

const PlanEditor = () => {
  const { planId } = useParams();
  const navigate = useNavigate();

  const [plan, setPlan] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Reference data for dropdowns
  const [zones, setZones] = useState([]);
  const [surfaces, setSurfaces] = useState([]);
  const [products, setProducts] = useState([]);
  const [methods, setMethods] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [controls, setControls] = useState([]);
  const [icons, setIcons] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [
          planRes, linesRes, zonesRes, surfRes, prodRes, methRes, freqRes, ctrlRes, iconsRes,
        ] = await Promise.all([
          api.get(`/plans/${planId}`),
          api.get(`/plan-lines/${planId}`),
          api.get('/zones'),
          api.get('/surface-items'),
          api.get('/products'),
          api.get('/methods'),
          api.get('/frequencies'),
          api.get('/controls'),
          api.get('/icons'),
        ]);
        setPlan(planRes.data);
        setLines(linesRes.data.map((l, idx) => ({ ...l, sort_order: l.sort_order ?? idx })));
        setZones(zonesRes.data);
        setSurfaces(surfRes.data);
        setProducts(prodRes.data);
        setMethods(methRes.data);
        setFrequencies(freqRes.data);
        setControls(ctrlRes.data);
        setIcons(iconsRes.data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || 'Echec chargement plan');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [planId]);

  // Surfaces are zone-agnostic since 2026-05: the full list is available
  // everywhere, sorted alphabetically (French collation for accents).
  const allSurfacesSorted = useMemo(
    () => [...surfaces].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
    ),
    [surfaces]
  );

  const updateLine = (idx, patch) => {
    setLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const onChangeZone = (idx, zoneId) => {
    // Surfaces are zone-agnostic since 2026-05: changing the zone keeps the
    // current surface selection. Only the row's zone metadata is updated.
    updateLine(idx, { zone_id: zoneId });
  };

  const onChangeProduct = (idx, productId) => {
    const p = products.find((x) => x.id === productId);
    updateLine(idx, {
      product_id: productId || null,
      // Auto-fill dosage/temp/method only when those fields are empty
      dosage: p && !lines[idx].dosage ? (p.dosage || '') : lines[idx].dosage,
      temperature_instruction:
        p && !lines[idx].temperature_instruction ? (p.temperature || '') : lines[idx].temperature_instruction,
      method_id: p && !lines[idx].method_id ? (p.default_method_id || null) : lines[idx].method_id,
    });
  };

  // Selecting a surface auto-syncs the icon_id from the surface_items master
  // (the surface's pictogram is the source of truth). The user can still
  // override per-line via the icon picker after selection.
  const onChangeSurface = (idx, surfaceId) => {
    const s = surfaces.find((x) => x.id === surfaceId);
    updateLine(idx, {
      surface_id: surfaceId || null,
      surface_item: s?.name || '',
      icon_id: s?.icon_id || null,
    });
  };

  const onChangeControls = (idx, controlIds) => {
    // controlIds is an array of selected control_id strings
    const namesMap = new Map(controls.map((c) => [c.id, c.name]));
    const names = controlIds.map((cid) => namesMap.get(cid) || '').filter(Boolean);
    updateLine(idx, {
      control_ids: controlIds,
      control_id: controlIds[0] || null,
      control: names.join(' + '),
    });
  };

  const insertLineAt = (idx, base = {}) => {
    setLines((prev) => {
      const next = [...prev];
      const inserted = { ...EMPTY_LINE, plan_id: planId, ...base };
      next.splice(idx + 1, 0, inserted);
      return next.map((l, i) => ({ ...l, sort_order: i }));
    });
  };

  const duplicateLine = (idx) => {
    const src = lines[idx];
    insertLineAt(idx, { ...src, id: null });
    toast.success('Ligne dupliquee');
  };

  const deleteLine = (idx) => {
    if (!window.confirm('Supprimer cette ligne ?')) return;
    setLines((prev) => prev.filter((_, i) => i !== idx).map((l, i) => ({ ...l, sort_order: i })));
  };

  const addLineAtEnd = () => {
    const firstZoneId = zones[0]?.id || '';
    setLines((prev) => [
      ...prev,
      { ...EMPTY_LINE, plan_id: planId, zone_id: firstZoneId, sort_order: prev.length },
    ]);
  };

  const addZoneTitleAtEnd = () => {
    setLines((prev) => [
      ...prev,
      {
        ...EMPTY_LINE,
        plan_id: planId,
        is_zone_title: true,
        zone_title_text: 'Nouvelle zone',
        zone_id: zones[0]?.id || '',
        sort_order: prev.length,
      },
    ]);
  };

  const buildPayload = () =>
    lines.map((l, i) => ({
      plan_id: planId,
      zone_id: l.zone_id || '',
      surface_item: l.surface_item || '',
      surface_id: l.surface_id || null,
      product_id: l.product_id || null,
      dosage: l.dosage || '',
      temperature_instruction: l.temperature_instruction || '',
      method_id: l.method_id || null,
      frequency_id: l.frequency_id || null,
      control: l.control || '',
      control_id: l.control_id || null,
      control_ids: Array.isArray(l.control_ids) ? l.control_ids : (l.control_id ? [l.control_id] : []),
      responsible: l.responsible || '',
      icon_id: l.icon_id || null,
      sort_order: i,
      is_zone_title: !!l.is_zone_title,
      zone_title_text: l.zone_title_text || '',
    }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      await api.put(`/plan-lines/bulk/${planId}`, payload);
      toast.success(`${payload.length} ligne(s) enregistree(s)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndGeneratePDF = async () => {
    setGenerating(true);
    try {
      await api.put(`/plan-lines/bulk/${planId}`, buildPayload());
      const token = localStorage.getItem('access_token');
      const url = `${BACKEND_BASE}/api/plans/${planId}/pdf?mode=preview&token=${encodeURIComponent(token)}`;
      window.open(url, '_blank');
      toast.success('PDF genere');
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || 'Erreur de generation');
    } finally {
      setGenerating(false);
    }
  };

  const zonesSorted = useMemo(
    () => [...zones].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
    ),
    [zones]
  );

  // Method-consistency: per-line check vs each line's product canonical method.
  // Computed locally so the badge updates instantly when the user edits a line.
  const productsById = useMemo(
    () => Object.fromEntries(products.map((p) => [p.id, p])),
    [products]
  );
  const driftedLines = useMemo(() => {
    return lines
      .map((l, idx) => {
        if (l.is_zone_title || !l.product_id) return null;
        const p = productsById[l.product_id];
        const expected = p?.default_method_id || null;
        if (!expected) return null;
        if ((l.method_id || null) === expected) return null;
        return { idx, line: l, expectedMethodId: expected };
      })
      .filter(Boolean);
  }, [lines, productsById]);

  const syncAllMethodsFromProducts = () => {
    if (driftedLines.length === 0) return;
    setLines((prev) => prev.map((l, idx) => {
      const d = driftedLines.find((x) => x.idx === idx);
      return d ? { ...l, method_id: d.expectedMethodId } : l;
    }));
    toast.success(`${driftedLines.length} ligne(s) synchronisee(s) avec la methode du produit`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20" style={{ color: '#0E2D43' }}>
          <Loader2 className="animate-spin mr-2" size={20} /> Chargement du plan...
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="plan-editor-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => navigate('/plans')}
              data-testid="back-to-plans-button"
              className="inline-flex items-center text-sm mb-2 hover:underline" style={{ color: '#4A90D9' }}>
              <ArrowLeft size={14} className="mr-1" /> Retour aux plans
            </button>
            <h1 className="text-3xl font-bold" style={{ color: '#0E2D43', fontFamily: 'Work Sans, sans-serif' }}>
              Editer le plan HACCP
            </h1>
            <p className="text-sm mt-1" style={{ color: '#6B7280' }}>
              {plan?.plan_name || 'Plan'} · {lines.filter((l) => !l.is_zone_title).length} ligne(s)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleSave} disabled={saving}
              data-testid="save-plan-button"
              className="flex items-center gap-2 px-4 py-2 rounded font-medium border disabled:opacity-50"
              style={{ borderColor: '#0E2D43', color: '#0E2D43' }}>
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Enregistrer
            </button>
            <button onClick={handleSaveAndGeneratePDF} disabled={generating}
              data-testid="save-generate-pdf-button"
              className="flex items-center gap-2 px-4 py-2 rounded font-medium disabled:opacity-50"
              style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
              {generating ? <Loader2 className="animate-spin" size={16} /> : <FileText size={16} />} Enregistrer et generer PDF
            </button>
          </div>
        </div>

        {driftedLines.length > 0 && (
          <div
            data-testid="method-consistency-banner"
            className="flex items-start gap-3 mb-4 px-4 py-3 rounded border"
            style={{ borderColor: '#FCD34D', backgroundColor: '#FFFBEB', color: '#78350F' }}
          >
            <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold leading-5">
                {driftedLines.length} ligne(s) utilisent une methode differente de celle du produit
              </p>
              <p className="text-xs mt-1 leading-5">
                Le PDF affichera ce que la ligne contient. Resynchronisez pour utiliser le protocole
                canonique du produit (Donnees metier &gt; Produit &gt; Methode associee).
              </p>
            </div>
            <button
              onClick={syncAllMethodsFromProducts}
              data-testid="sync-all-methods-button"
              className="text-xs px-3 py-1.5 rounded font-medium flex-shrink-0"
              style={{ backgroundColor: '#92400E', color: '#FFFFFF' }}
            >
              Resynchroniser tout
            </button>
          </div>
        )}

        <PlanEditorTable
          lines={lines}
          zones={zonesSorted}
          allSurfaces={allSurfacesSorted}
          products={products}
          methods={methods}
          frequencies={frequencies}
          controls={controls}
          icons={icons}
          updateLine={updateLine}
          onChangeZone={onChangeZone}
          onChangeSurface={onChangeSurface}
          onChangeProduct={onChangeProduct}
          onChangeControls={onChangeControls}
          duplicateLine={duplicateLine}
          deleteLine={deleteLine}
          insertLineAt={insertLineAt}
        />

        <div className="mt-4 flex gap-2">
          <button onClick={addLineAtEnd}
            data-testid="add-line-button"
            className="flex items-center gap-2 px-3 py-2 rounded text-sm border"
            style={{ borderColor: '#0E2D43', color: '#0E2D43' }}>
            <Plus size={14} /> Ajouter une ligne
          </button>
          <button onClick={addZoneTitleAtEnd}
            data-testid="add-zone-title-button"
            className="flex items-center gap-2 px-3 py-2 rounded text-sm border"
            style={{ borderColor: '#0E2D43', color: '#0E2D43' }}>
            <Plus size={14} /> Ajouter un titre de zone
          </button>
        </div>
      </div>
    </Layout>
  );
};


const PlanEditorTable = ({
  lines, zones, allSurfaces, products, methods, frequencies, controls, icons,
  updateLine, onChangeZone, onChangeSurface, onChangeProduct, onChangeControls,
  duplicateLine, deleteLine,
}) => {
  return (
    <div className="bg-white rounded-lg shadow border overflow-x-auto" style={{ borderColor: '#E5E7EB' }}>
      <table className="w-full text-xs">
        <thead style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
          <tr>
            <th className="px-2 py-2 text-left font-medium w-32">Zone</th>
            <th className="px-2 py-2 text-left font-medium w-32">Surface</th>
            <th className="px-2 py-2 text-left font-medium w-40">Produit</th>
            <th className="px-2 py-2 text-left font-medium w-24">Dosage</th>
            <th className="px-2 py-2 text-left font-medium w-24">Temperature</th>
            <th className="px-2 py-2 text-left font-medium w-32">Methode</th>
            <th className="px-2 py-2 text-left font-medium w-28">Frequence</th>
            <th className="px-2 py-2 text-left font-medium w-28">Controle</th>
            <th className="px-2 py-2 text-left font-medium w-24">Responsable</th>
            <th className="px-2 py-2 text-right font-medium w-24">Actions</th>
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 && (
            <tr>
              <td colSpan="10" className="px-3 py-8 text-center" style={{ color: '#6B7280' }}>
                Aucune ligne. Cliquez sur &quot;Ajouter une ligne&quot; pour commencer.
              </td>
            </tr>
          )}
          {lines.map((line, idx) => {
            if (line.is_zone_title) {
              return (
                <tr key={idx} data-testid={`plan-row-title-${idx}`}
                  style={{ backgroundColor: '#0E2D430D' }}>
                  <td colSpan="9" className="px-3 py-2">
                    <input
                      type="text"
                      value={line.zone_title_text || ''}
                      onChange={(e) => updateLine(idx, { zone_title_text: e.target.value })}
                      placeholder="Titre de zone (ex: PREPARATION CHAUDE)"
                      data-testid={`row-${idx}-zone-title-input`}
                      className="w-full bg-transparent px-2 py-1 rounded font-bold uppercase tracking-wider"
                      style={{ color: '#0E2D43', borderBottom: '2px solid #0E2D43' }}
                    />
                  </td>
                  <td className="px-2 py-2 text-right">
                    <RowActions idx={idx} onDup={duplicateLine} onDel={deleteLine} />
                  </td>
                </tr>
              );
            }
            return (
              <PlanEditorRow
                key={idx}
                idx={idx}
                line={line}
                zones={zones}
                surfaces={allSurfaces}
                products={products}
                methods={methods}
                frequencies={frequencies}
                controls={controls}
                icons={icons}
                updateLine={updateLine}
                onChangeZone={onChangeZone}
                onChangeSurface={onChangeSurface}
                onChangeProduct={onChangeProduct}
                onChangeControls={onChangeControls}
                onDup={duplicateLine}
                onDel={deleteLine}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
};


const MethodCellWithSync = ({ line, products, methods, onChange, dataTestId }) => {
  // Look up the product's canonical default method (Données métier → Produit).
  // The protocole shown in the PDF should follow this method. When the line
  // carries a stale snapshot, we surface the discrepancy + a 1-click resync.
  const product = (products || []).find((p) => p.id === line.product_id) || null;
  const expectedId = product?.default_method_id || null;
  const currentId = line.method_id || null;
  const expected = methods.find((m) => m.id === expectedId) || null;
  const drifted = !!(product && expectedId && currentId && currentId !== expectedId);
  // Also flag the "missing method" case when the product has a canonical one
  // but the line currently has no method assigned.
  const missing = !!(product && expectedId && !currentId);

  return (
    <div className="space-y-1">
      <SelectOrUnref
        value={currentId}
        label=""
        options={methods}
        onChange={onChange}
        dataTestId={dataTestId}
      />
      {(drifted || missing) && expected && (
        <button
          type="button"
          onClick={() => onChange(expectedId)}
          data-testid={`${dataTestId}-sync`}
          title={drifted
            ? `La methode actuelle differe de la methode canonique du produit (${expected.name}). Cliquer pour resynchroniser.`
            : `Aucune methode assignee. La methode du produit est ${expected.name}. Cliquer pour l'appliquer.`}
          className="w-full flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
          style={{
            backgroundColor: drifted ? '#FEF3C7' : '#EFF6FF',
            color: drifted ? '#92400E' : '#1E40AF',
            border: `1px solid ${drifted ? '#FCD34D' : '#BFDBFE'}`,
          }}
        >
          <AlertTriangle size={9} />
          <span className="truncate">{drifted ? 'Methode produit:' : 'Suggestion:'} {expected.name}</span>
        </button>
      )}
    </div>
  );
};

const SurfaceSelect = ({ line, surfaces, icons, onChangeSurface, onChangeIcon, dataTestId }) => {
  const [open, setOpen] = React.useState(false);
  const [showIconPicker, setShowIconPicker] = React.useState(false);
  const ref = React.useRef(null);
  const iconPickerRef = React.useRef(null);

  React.useEffect(() => {
    if (!open && !showIconPicker) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)
        && (!iconPickerRef.current || !iconPickerRef.current.contains(e.target))) {
        setOpen(false);
        setShowIconPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, showIconPicker]);

  // Index icons by id (for thumbnail rendering) and filter to surface category
  const iconsById = React.useMemo(() => Object.fromEntries(icons.map((i) => [i.id, i])), [icons]);
  const surfaceIcons = React.useMemo(
    () => icons.filter((i) => (i.category || '').toLowerCase() === 'surface'),
    [icons]
  );

  // Pictogram displayed in the trigger button. Priority chain mirrors the PDF:
  //   1. line.icon_id (per-line override)
  //   2. surfaces[selected].icon_id (master surface default)
  //   3. name-based fallback against surface-category icons (insensitive)
  const matched = surfaces.find((s) => s.id === line.surface_id);
  const isUnref = !!line.surface_item && !matched && !line.surface_id;

  const norm = (s) => (s || '').toString().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const resolveIcon = () => {
    if (line.icon_id && iconsById[line.icon_id]) return iconsById[line.icon_id];
    if (matched?.icon_id && iconsById[matched.icon_id]) return iconsById[matched.icon_id];
    const target = norm(matched?.name || line.surface_item);
    if (!target) return null;
    return surfaceIcons.find((i) => norm(i.name) === target) || null;
  };
  const currentIcon = resolveIcon();
  // Effective icon_id for "override active" indicator (line.icon_id explicitly set
  // and different from the surface master default).
  const masterIconId = matched?.icon_id || null;
  const overrideActive = !!line.icon_id && line.icon_id !== masterIconId;

  const select = (sid) => {
    onChangeSurface(sid);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <div className="flex items-stretch gap-1">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={dataTestId}
          className="flex-1 min-h-[28px] px-1.5 py-0.5 border rounded text-xs text-left flex items-center gap-1.5"
          style={{
            borderColor: isUnref ? '#F59E0B' : '#D1D5DB',
            backgroundColor: isUnref ? '#FFFBEB' : '#FFFFFF',
          }}
        >
          {currentIcon?.image_url ? (
            <img
              src={`${BACKEND_BASE}${currentIcon.image_url}`}
              alt=""
              data-testid={`${dataTestId}-thumb`}
              className="w-5 h-5 object-contain flex-shrink-0"
              style={{ backgroundColor: '#F9FAFB', borderRadius: 2 }}
            />
          ) : (
            <span
              data-testid={`${dataTestId}-thumb-empty`}
              className="w-5 h-5 inline-flex items-center justify-center flex-shrink-0 rounded"
              style={{ backgroundColor: '#F3F4F6', color: '#9CA3AF' }}
            >
              <ImageIcon size={11} />
            </span>
          )}
          <span className="truncate" style={{ color: matched ? '#0E2D43' : '#6B7280' }}>
            {matched?.name || line.surface_item || '-- Choisir --'}
          </span>
          {isUnref && (
            <span
              data-testid={`${dataTestId}-unref-badge`}
              className="ml-auto inline-flex items-center text-[9px] rounded-full px-1 py-0.5 font-bold flex-shrink-0"
              style={{ backgroundColor: '#F59E0B', color: '#FFFFFF' }}
              title="Valeur Excel non referencee"
            >
              <AlertTriangle size={9} />
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => { setShowIconPicker((v) => !v); setOpen(false); }}
          data-testid={`${dataTestId}-icon-override-button`}
          title={overrideActive ? 'Icone personnalisee (cliquer pour changer)' : "Changer l'icone"}
          className="px-1.5 rounded border flex items-center justify-center"
          style={{
            borderColor: overrideActive ? '#0E2D43' : '#D1D5DB',
            backgroundColor: overrideActive ? '#0E2D43' : '#FFFFFF',
            color: overrideActive ? '#FFFFFF' : '#6B7280',
          }}
        >
          <ImageIcon size={12} />
        </button>
      </div>

      {open && (
        <div
          data-testid={`${dataTestId}-dropdown`}
          className="absolute z-30 mt-1 w-64 max-h-72 overflow-y-auto rounded shadow-lg border py-1"
          style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB' }}
        >
          {surfaces.length === 0 && (
            <div className="px-2 py-2 text-xs" style={{ color: '#9CA3AF' }}>
              Aucune surface dans cette zone
            </div>
          )}
          {surfaces.map((s) => {
            const sIcon = (s.icon_id && iconsById[s.icon_id]) || surfaceIcons.find((i) => norm(i.name) === norm(s.name)) || null;
            const active = s.id === line.surface_id;
            return (
              <button
                type="button"
                key={s.id}
                onClick={() => select(s.id)}
                data-testid={`${dataTestId}-option-${s.id}`}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-gray-50 text-left"
                style={{ color: '#0E2D43', backgroundColor: active ? '#EFF6FF' : 'transparent' }}
              >
                {sIcon?.image_url ? (
                  <img
                    src={`${BACKEND_BASE}${sIcon.image_url}`}
                    alt=""
                    className="w-6 h-6 object-contain flex-shrink-0"
                    style={{ backgroundColor: '#F9FAFB', borderRadius: 2 }}
                  />
                ) : (
                  <span className="w-6 h-6 inline-flex items-center justify-center flex-shrink-0 rounded"
                    style={{ backgroundColor: '#F3F4F6', color: '#9CA3AF' }}>
                    <ImageIcon size={12} />
                  </span>
                )}
                <span className="truncate">{s.name}</span>
                {active && <span className="ml-auto" style={{ color: '#1D4ED8' }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}

      {showIconPicker && (
        <div
          ref={iconPickerRef}
          data-testid={`${dataTestId}-icon-picker`}
          className="absolute z-30 right-0 mt-1 w-72 max-h-80 rounded shadow-lg border"
          style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB' }}
        >
          <div className="flex items-center justify-between px-2 py-1.5 border-b" style={{ borderColor: '#E5E7EB' }}>
            <span className="text-xs font-medium" style={{ color: '#0E2D43' }}>
              Choisir un pictogramme
            </span>
            <div className="flex items-center gap-1">
              {overrideActive && (
                <button
                  type="button"
                  onClick={() => { onChangeIcon(null); setShowIconPicker(false); }}
                  data-testid={`${dataTestId}-icon-reset`}
                  className="text-[10px] px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
                  title="Revenir a l'icone par defaut de la surface"
                >
                  Reinitialiser
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowIconPicker(false)}
                className="p-0.5 rounded hover:bg-gray-100"
                aria-label="Fermer"
              >
                <X size={12} />
              </button>
            </div>
          </div>
          <div className="p-2 grid grid-cols-4 gap-1 max-h-64 overflow-y-auto">
            {surfaceIcons.length === 0 && (
              <div className="col-span-4 text-center text-xs py-4" style={{ color: '#9CA3AF' }}>
                Aucune icone de categorie &quot;Surface&quot;.
              </div>
            )}
            {surfaceIcons.map((ic) => {
              const sel = line.icon_id === ic.id
                || (!line.icon_id && currentIcon && currentIcon.id === ic.id);
              return (
                <button
                  type="button"
                  key={ic.id}
                  onClick={() => { onChangeIcon(ic.id); setShowIconPicker(false); }}
                  data-testid={`${dataTestId}-icon-option-${ic.id}`}
                  title={ic.name}
                  className="flex flex-col items-center gap-1 p-1 rounded border"
                  style={{
                    borderColor: sel ? '#0E2D43' : '#E5E7EB',
                    backgroundColor: sel ? '#EFF6FF' : '#FFFFFF',
                  }}
                >
                  {ic.image_url ? (
                    <img
                      src={`${BACKEND_BASE}${ic.image_url}`}
                      alt={ic.name}
                      className="w-10 h-10 object-contain"
                    />
                  ) : (
                    <span className="w-10 h-10 inline-flex items-center justify-center" style={{ color: '#9CA3AF' }}>
                      <ImageIcon size={16} />
                    </span>
                  )}
                  <span className="text-[9px] truncate w-full text-center" style={{ color: '#0E2D43' }}>
                    {ic.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};


const MultiControlSelect = ({ line, controls, onChange, dataTestId }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Selected ids: prefer line.control_ids array, fallback to single control_id
  const selectedIds = React.useMemo(() => {
    if (Array.isArray(line.control_ids) && line.control_ids.length > 0) return line.control_ids;
    if (line.control_id) return [line.control_id];
    return [];
  }, [line.control_ids, line.control_id]);

  const labelById = React.useMemo(() => Object.fromEntries(controls.map((c) => [c.id, c.name])), [controls]);

  // Detect Excel-imported text that doesn't match any control (badge orange)
  const unrefText = !selectedIds.length && line.control ? line.control : '';

  const toggle = (cid) => {
    const next = selectedIds.includes(cid)
      ? selectedIds.filter((x) => x !== cid)
      : [...selectedIds, cid];
    onChange(next);
  };

  const remove = (cid, e) => {
    e?.stopPropagation();
    onChange(selectedIds.filter((x) => x !== cid));
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={dataTestId}
        className="w-full min-h-[28px] px-1.5 py-1 border rounded text-xs text-left flex flex-wrap gap-1 items-center"
        style={{
          borderColor: unrefText ? '#F59E0B' : '#D1D5DB',
          backgroundColor: unrefText ? '#FFFBEB' : '#FFFFFF',
        }}
      >
        {selectedIds.length === 0 && !unrefText && (
          <span style={{ color: '#9CA3AF' }}>-- Choisir --</span>
        )}
        {unrefText && (
          <span
            data-testid={`${dataTestId}-unref-badge`}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
            style={{ backgroundColor: '#F59E0B', color: '#FFFFFF' }}
            title="Valeur Excel non referencee"
          >
            {unrefText}
          </span>
        )}
        {selectedIds.map((cid) => (
          <span
            key={cid}
            data-testid={`${dataTestId}-chip-${cid}`}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
            style={{ backgroundColor: '#E0F2FE', color: '#075985' }}
          >
            {labelById[cid] || cid}
            <span
              role="button"
              onClick={(e) => remove(cid, e)}
              data-testid={`${dataTestId}-chip-${cid}-remove`}
              className="ml-0.5 cursor-pointer"
              style={{ color: '#075985' }}
            >
              ×
            </span>
          </span>
        ))}
      </button>
      {open && (
        <div
          data-testid={`${dataTestId}-dropdown`}
          className="absolute z-30 mt-1 w-56 max-h-72 overflow-y-auto rounded shadow-lg border py-1"
          style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB' }}
        >
          {controls.map((c) => {
            const active = selectedIds.includes(c.id);
            return (
              <button
                type="button"
                key={c.id}
                onClick={() => toggle(c.id)}
                data-testid={`${dataTestId}-option-${c.id}`}
                className="w-full flex items-center justify-between px-2 py-1.5 text-xs hover:bg-gray-50"
                style={{ color: '#0E2D43' }}
              >
                <span>{c.name}</span>
                {active && <span style={{ color: '#075985' }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};


const SelectOrUnref = ({ value, label, options, onChange, dataTestId, placeholder = '-- Choisir --' }) => {
  // value = the actual selected id; label = the raw imported text we should fall
  // back to when no option matches and value is null.
  const matched = options.find((o) => o.id === value);
  const isUnref = !!label && !matched && !value;
  return (
    <div className="relative">
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={dataTestId}
        className="w-full px-2 py-1 border rounded text-xs"
        style={{
          borderColor: isUnref ? '#F59E0B' : '#D1D5DB',
          backgroundColor: isUnref ? '#FFFBEB' : '#FFFFFF',
        }}
      >
        <option value="">{placeholder}</option>
        {isUnref && (
          <option value="" disabled>
            ⚠ {label} (non reference)
          </option>
        )}
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name || o.label}</option>
        ))}
      </select>
      {isUnref && (
        <span
          data-testid={`${dataTestId}-unref-badge`}
          className="absolute -top-1 -right-1 inline-flex items-center text-[9px] rounded-full px-1 py-0.5 font-bold"
          style={{ backgroundColor: '#F59E0B', color: '#FFFFFF' }}
          title="Valeur Excel non referencee. Choisissez une valeur dans la liste ou laissez tel quel."
        >
          <AlertTriangle size={9} className="mr-0.5" /> {label}
        </span>
      )}
    </div>
  );
};


const PlanEditorRow = ({
  idx, line, zones, surfaces, products, methods, frequencies, controls, icons,
  updateLine, onChangeZone, onChangeSurface, onChangeProduct, onChangeControls, onDup, onDel,
}) => {
  return (
    <tr data-testid={`plan-row-${idx}`} className="border-t" style={{ borderColor: '#E5E7EB' }}>
      <td className="px-2 py-1.5">
        <SelectOrUnref
          value={line.zone_id}
          label={line.zone_title_text}
          options={zones}
          onChange={(v) => onChangeZone(idx, v)}
          dataTestId={`row-${idx}-zone`}
        />
      </td>
      <td className="px-2 py-1.5">
        <SurfaceSelect
          line={line}
          surfaces={surfaces}
          icons={icons}
          onChangeSurface={(v) => onChangeSurface(idx, v)}
          onChangeIcon={(iconId) => updateLine(idx, { icon_id: iconId })}
          dataTestId={`row-${idx}-surface`}
        />
      </td>
      <td className="px-2 py-1.5">
        <SelectOrUnref
          value={line.product_id}
          label={line.product_id ? '' : ''}
          options={products}
          onChange={(v) => onChangeProduct(idx, v)}
          dataTestId={`row-${idx}-product`}
        />
      </td>
      <td className="px-2 py-1.5">
        <input type="text" value={line.dosage || ''}
          onChange={(e) => updateLine(idx, { dosage: e.target.value })}
          data-testid={`row-${idx}-dosage`}
          className="w-full px-2 py-1 border rounded text-xs" style={{ borderColor: '#D1D5DB' }} />
      </td>
      <td className="px-2 py-1.5">
        <input type="text" value={line.temperature_instruction || ''}
          onChange={(e) => updateLine(idx, { temperature_instruction: e.target.value })}
          data-testid={`row-${idx}-temp`}
          className="w-full px-2 py-1 border rounded text-xs" style={{ borderColor: '#D1D5DB' }} />
      </td>
      <td className="px-2 py-1.5">
        <MethodCellWithSync
          line={line}
          products={products}
          methods={methods}
          onChange={(v) => updateLine(idx, { method_id: v })}
          dataTestId={`row-${idx}-method`}
        />
      </td>
      <td className="px-2 py-1.5">
        <SelectOrUnref
          value={line.frequency_id}
          label=""
          options={frequencies}
          onChange={(v) => updateLine(idx, { frequency_id: v })}
          dataTestId={`row-${idx}-frequency`}
        />
      </td>
      <td className="px-2 py-1.5">
        <MultiControlSelect
          line={line}
          controls={controls}
          onChange={(ids) => onChangeControls(idx, ids)}
          dataTestId={`row-${idx}-control`}
        />
      </td>
      <td className="px-2 py-1.5">
        <input type="text" value={line.responsible || ''}
          onChange={(e) => updateLine(idx, { responsible: e.target.value })}
          data-testid={`row-${idx}-responsible`}
          className="w-full px-2 py-1 border rounded text-xs" style={{ borderColor: '#D1D5DB' }} />
      </td>
      <td className="px-2 py-1.5 text-right">
        <RowActions idx={idx} onDup={onDup} onDel={onDel} />
      </td>
    </tr>
  );
};

const RowActions = ({ idx, onDup, onDel }) => (
  <div className="inline-flex gap-1">
    <button onClick={() => onDup(idx)}
      data-testid={`row-${idx}-duplicate`}
      title="Dupliquer"
      className="p-1 rounded" style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8' }}>
      <Copy size={13} />
    </button>
    <button onClick={() => onDel(idx)}
      data-testid={`row-${idx}-delete`}
      title="Supprimer"
      className="p-1 rounded" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
      <Trash2 size={13} />
    </button>
  </div>
);

export default PlanEditor;
